// @ts-expect-error pure js
import axios from "../../api";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
// @ts-expect-error pure js
import { getPromoCode, getUtmParams } from "../../utils";
import { signupTrackingFields } from "../../utils/tracking";
// @ts-expect-error pure js
import useAuth from "../../hooks/use-auth.js";
import styles from "./AppleAuth.module.css";

const APPLE_SDK_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/fr_FR/appleid.auth.js";

interface AppleSignInResponse {
  authorization: { id_token: string; code: string };
}

interface AppleIDAuth {
  init(config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    usePopup: boolean;
  }): void;
  signIn(): Promise<AppleSignInResponse>;
}

declare global {
  interface Window {
    AppleID?: { auth: AppleIDAuth };
  }
}

let sdkPromise: Promise<AppleIDAuth> | null = null;

function loadAppleSdk(): Promise<AppleIDAuth> {
  sdkPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = APPLE_SDK_SRC;
    script.onload = () => {
      if (!window.AppleID) {
        reject(new Error("AppleID SDK missing after load"));
        return;
      }
      window.AppleID.auth.init({
        clientId: import.meta.env.VITE_APPLE_SERVICES_ID,
        scope: "email",
        // Doit être enregistrée dans les Return URLs du Services ID Apple.
        redirectURI: window.location.origin,
        usePopup: true,
      });
      resolve(window.AppleID.auth);
    };
    script.onerror = () => reject(new Error("AppleID SDK load failed"));
    document.head.appendChild(script);
  });
  return sdkPromise;
}

interface AppleAuthProps {
  persistLogin?: boolean;
}

export default function AppleAuth({ persistLogin = false }: AppleAuthProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { buildAndSetAuth } = useAuth();
  const from =
    (location.state?.from?.pathname ?? "/") +
    (location.state?.from?.search ?? "");
  const [searchParams] = useSearchParams();
  const idParrain = searchParams.get("referral") || location.state?.referral;
  const utm = getUtmParams(searchParams) || location.state?.utm;
  const codePromo = getPromoCode(searchParams) || location.state?.promo;

  async function signIn() {
    let signInResponse: AppleSignInResponse;
    try {
      signInResponse = await (await loadAppleSdk()).signIn();
    } catch {
      // Popup fermée par l'utilisateur ou SDK indisponible.
      return;
    }
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/signup/apple`,
        {
          token: signInResponse.authorization.id_token,
          id_parrain: idParrain,
          code_promo_inscription: codePromo,
          utm,
          ...signupTrackingFields(searchParams),
          persist_login: persistLogin,
        },
      );
      buildAndSetAuth(response.data);
      navigate(response.data.home === "/tbd" ? response.data.home : from, {
        replace: true,
      });
    } catch (err: unknown) {
      const error = err as { response?: { status: number } };
      if (error.response?.status !== 401) {
        console.error(err);
      }
    }
  }

  return (
    <button type="button" className={styles.appleButton} onClick={signIn}>
      <svg viewBox="0 0 384 512" width="16" height="16" aria-hidden="true">
        <path
          fill="currentColor"
          d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
        />
      </svg>
      Se connecter avec Apple
    </button>
  );
}
